import React, { useCallback, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { AccessRoleDTO, AccessRoleFilterDTO, ApplicationDTO, ApplicationFilterDTO, FeatureDTO, UserDTO } from '../../../shared/interfaces';
import { formatDateTime } from '../../../shared/utils';

interface IFeatureMaintenanceProps {
    data: any;
    parentId?: string;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: true) => void;
}

export const FeatureMaintenance: React.FC<IFeatureMaintenanceProps> = ({ data, parentId, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {
    const [applicationList, setApplicationList] = useState<ApplicationDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);
    const [featureDTO, setFeatureDTO] = useState<FeatureDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setFeatureDTO({
            ...featureDTO,
            id: '',
            name: '',
            pathUrl: '',
            description: '',
            applicationId: parentId ?? ''
        } as FeatureDTO);

        if(isOpenMaintenance){
            handleApplicationGetAllAsync();
            handleUserGetAll();
            handleGetByIdAsync();

            handleGetPermission(ListId.appsphere.featureId, setAccessRolePermissionDTO);

        }
    }, [data, isOpenMaintenance]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync',accessRoleFilterDTO, setActionExecuted, setLoading, undefined,  setList,);
    }, []);
    
    const handleUserGetAll = useCallback(async () => {
        const filterUser = {
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('authsphere.user', filterUser, setLoading, setUserList, showMessage);
    }, []);

    const handleApplicationGetAllAsync = useCallback(async () => {
        const applicationFilterDTO: ApplicationFilterDTO = {
            name: '',
            typeId: '',
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('appsphere.application', applicationFilterDTO, setLoading, setApplicationList, showMessage);
    }, []);

    const handleGetByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('appsphere.feature', data.id, setLoading, setFeatureDTO, showMessage);
    }, [data]);

    const handleSaveClick = useCallback(async () => {
        const dto = featureDTO as FeatureDTO;
        if (featureDTO?.id === '' || featureDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            GenericService.AddAsync1('appsphere.feature', dto, setFeatureDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            if(!accessRolePermissionDTO?.allowUpdate) {
                showMessage('There is no permission for this access.', 'info');
                return;
            }
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('appsphere.feature', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [featureDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);
    }

    const actions: IDialogActions[] = [
        { id: 'save', text: 'Save', icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick },
        { id: 'cancel', text: 'Cancel', icon: 'cancel_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title='FEATURE DETAILS'
                icon='widgets_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={9}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                value={featureDTO?.name}
                                setValue={setFeatureDTO}
                                obj={featureDTO}
                                required={true}
                                maxLength={200}
                                autoFocus={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='applicationId'
                                label='Application Name'
                                value={featureDTO?.applicationId}
                                setValue={setFeatureDTO}
                                obj={featureDTO}
                                valueList={applicationList}
                                disabled={parentId ?? featureDTO?.id ? true : false}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='pathUrl'
                                label='Path Url'
                                value={featureDTO?.pathUrl}
                                setValue={setFeatureDTO}
                                obj={featureDTO}
                                required={false}
                                maxLength={400}
                            />
                        </Grid>

                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='description'
                                label='Description'
                                value={featureDTO?.description}
                                setValue={setFeatureDTO}
                                obj={featureDTO}
                                required={true}
                                multiline={true}
                                rows={2}
                            />
                        </Grid>
                        {featureDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={featureDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label='Created By'
                                        value={userList.find(x => x.id === featureDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label='Creation Date'
                                        value={formatDateTime(String(featureDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label='Updated By'
                                        value={userList.find(x => x.id === featureDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label='Update Date'
                                        value={formatDateTime(String(featureDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};